import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import { AUDITION_TYPES, AUDITION_STATUSES } from "@/lib/crm";

const isDatetime = (kind) =>
  ["in_person", "callback_in_person", "callback_online", "chemistry_read",
   "screen_test", "final_audition", "online_live", "other"].includes(kind);
const isSelfTape = (kind) => kind === "self_tape";
const isOnline = (kind) => ["online_live", "callback_online"].includes(kind);
const isInPerson = (kind) =>
  ["in_person", "callback_in_person", "chemistry_read", "screen_test", "final_audition"].includes(kind);

const empty = {
  kind: "in_person",
  date: "", deadline: "",
  place: "", address: "", studio: "",
  link: "", platform: "",
  instructions: "", notes: "",
  status: "new",
};

export default function StageDialog({ open, onOpenChange, auditionId, stage, onSaved }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const isEdit = !!stage;
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (stage) {
      setForm({
        ...empty, ...stage,
        date: (stage.date || "").slice(0, 16),         // datetime-local
        deadline: (stage.deadline || "").slice(0, 16),
      });
    } else {
      setForm(empty);
    }
  }, [open, stage]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    const payload = {
      ...form,
      date: form.date ? new Date(form.date).toISOString() : null,
      deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
    };
    try {
      let r;
      if (isEdit) {
        r = await api.patch(`/crm/auditions/${auditionId}/stages/${stage.stage_id}`, payload);
      } else {
        r = await api.post(`/crm/auditions/${auditionId}/stages`, payload);
      }
      toast.success(t("stage.saved"));
      onOpenChange?.(false);
      onSaved?.(r.data);
    } catch { toast.error(t("stage.save_fail")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="stage-dialog"
        className="max-w-2xl bg-stone-950 border-stone-800 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="serif text-2xl text-stone-100">
            {isEdit ? t("stage.edit") : t("stage.add")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-cap">{t("stage.kind")}</label>
              <select
                data-testid="stage-kind"
                value={form.kind}
                onChange={(e) => set("kind", e.target.value)}
                className="script-input w-full"
              >
                {AUDITION_TYPES.map((k) => (
                  <option key={k} value={k}>{t(`aud.type.${k}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-cap">{t("stage.status")}</label>
              <select
                data-testid="stage-status"
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
                className="script-input w-full"
              >
                {AUDITION_STATUSES.map((s) => (
                  <option key={s} value={s}>{t(`aud.status.${s}`)}</option>
                ))}
              </select>
            </div>
          </div>

          {isDatetime(form.kind) && !isSelfTape(form.kind) && (
            <div>
              <label className="label-cap">{t("stage.date")}</label>
              <input
                data-testid="stage-date"
                type="datetime-local"
                value={form.date || ""}
                onChange={(e) => set("date", e.target.value)}
                className="script-input w-full"
              />
            </div>
          )}

          {isSelfTape(form.kind) && (
            <div>
              <label className="label-cap">{t("stage.deadline")}</label>
              <input
                data-testid="stage-deadline"
                type="datetime-local"
                value={form.deadline || ""}
                onChange={(e) => set("deadline", e.target.value)}
                className="script-input w-full"
              />
            </div>
          )}

          {isInPerson(form.kind) && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-cap">{t("stage.place")}</label>
                  <input
                    data-testid="stage-place"
                    value={form.place}
                    onChange={(e) => set("place", e.target.value)}
                    className="script-input w-full"
                  />
                </div>
                <div>
                  <label className="label-cap">{t("stage.studio")}</label>
                  <input
                    data-testid="stage-studio"
                    value={form.studio}
                    onChange={(e) => set("studio", e.target.value)}
                    className="script-input w-full"
                  />
                </div>
              </div>
              <div>
                <label className="label-cap">{t("stage.address")}</label>
                <input
                  data-testid="stage-address"
                  value={form.address}
                  onChange={(e) => set("address", e.target.value)}
                  className="script-input w-full"
                />
              </div>
            </>
          )}

          {isOnline(form.kind) && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-cap">{t("stage.platform")}</label>
                <input
                  data-testid="stage-platform"
                  value={form.platform}
                  onChange={(e) => set("platform", e.target.value)}
                  placeholder="Zoom / Google Meet / Teams…"
                  className="script-input w-full"
                />
              </div>
              <div>
                <label className="label-cap">{t("stage.link")}</label>
                <input
                  data-testid="stage-link"
                  type="url"
                  value={form.link}
                  onChange={(e) => set("link", e.target.value)}
                  placeholder="https://…"
                  className="script-input w-full"
                />
              </div>
            </div>
          )}

          <div>
            <label className="label-cap">{t("stage.instructions")}</label>
            <textarea
              data-testid="stage-instructions"
              value={form.instructions}
              onChange={(e) => set("instructions", e.target.value)}
              rows={2}
              className="script-input w-full resize-none"
            />
          </div>
          <div>
            <label className="label-cap">{t("stage.notes")}</label>
            <textarea
              data-testid="stage-notes"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={2}
              className="script-input w-full resize-none"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange?.(false)} className="ghost-btn">
              {t("action.cancel")}
            </button>
            <button
              type="submit"
              data-testid="stage-save"
              disabled={saving}
              className="pill-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {t("action.save")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
